/** @jsxImportSource @emotion/react */
import { useAtom } from 'jotai';
import useNotifications from '../../../../shared/hooks/use-notifications';
import { getUserChannelData, shouldHideDTDetails } from '../../../login/v1/utility';
import useAddonPurchase from '../../../../shared/hooks/use-addon-purchase';
import { facebookCapiUpgradeModalOpenAtom } from '../../../ai-bot/state';
import { useCallback } from 'react';
import { openPopup } from '../../../../shared/utils/popup';
import QSModal from '../../../../shared/components/molecules/modal';
import { css } from '@emotion/react';
import { QSButton, QSIconButton, QSTypography } from '../../../../shared/components/atoms';
import ProgressiveImage from '../../../../shared/components/atoms/progressive-image';
import { PlanAddonTypes } from '../../../renew-plan/v1/typings';
import { Close } from '@mui/icons-material';
import Loader from '../../../../shared/components/molecules/loader';
import SingleTickIcon from '../../../../shared/icons/SingleTick';
import FacebookConversionAPI from '../../../../shared/assets/img/FacebookConversionApi.png';
import MetaConversionIcon from '../../../../shared/assets/img/MetaConversionIcon.png';
import { getUrlWithToken } from '../../../../shared/utils/getMediaURLWithToken';
import { useGetToken } from '../../../../shared/hooks/useGetToken';

const FEATURES = [
  {
    featureLabel: 'Leads-to-sales conversion rate jump from 9% → 32.5%',
  },
  {
    featureLabel: '28% lower cost per purchase',
  },
  {
    featureLabel: '35% better RoAS vs non-CAPI campaigns',
  },
  {
    featureLabel: 'Full-funnel visibility—directly inside Meta Ads Manager',
  },
  {
    featureLabel: 'Dramatic drop in junk leads = more time for your sales team to close real deals',
  },
];

export default function FacebookCapiUpgradeBlocker() {
  const [upgradeModal, setUpgradeModal] = useAtom(facebookCapiUpgradeModalOpenAtom);
  const { addNotification } = useNotifications();
  const channelData = getUserChannelData();
  const hideDTDetails = shouldHideDTDetails();
  const { purchaseAddon, loading: purchaseLoading } = useAddonPurchase();
  let contactUsLink = `https://wa.me/${channelData?.supportNumber}?text=I%27m%20interested%20in%20buying%20LeadSquared%20Addon`;
  const token = useGetToken() || '';

  const onClose = useCallback(() => {
    setUpgradeModal(false);
  }, [setUpgradeModal]);

  const onClickContactUs = useCallback(async () => {
    try {
      if (hideDTDetails) {
        openPopup(contactUsLink, '_blank');
      } else {
        await purchaseAddon(PlanAddonTypes.CapiSupportAddon);
      }
      onClose();
    } catch (error) {
      addNotification({
        message: (error as Error).message ?? 'An error occurred. Please try again',
        type: 'error',
      });
    }
  }, [addNotification, onClose]);

  if (!upgradeModal) {
    return null;
  }

  return (
    <QSModal
      css={css`
        display: flex;
        justify-content: center;
        align-items: center;
      `}
      open={upgradeModal}
      onClose={onClose}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          border-radius: 8px;
          overflow: hidden;
          background: white;
          outline: none;
          position: relative;
          padding: 16px;
          max-width: 700px;
          gap: 20px;
        `}
      >
        <QSIconButton
          css={css`
            position: absolute;
            top: 8px;
            right: 8px;
          `}
          sx={{}}
          onClick={onClose}
        >
          <Close />
        </QSIconButton>
        <div
          css={css`
            overflow: auto;
            display: flex;
            align-items: flex-start;
            flex-wrap: wrap;
          `}
        >
          <div
            css={css`
              background-color: #f6f1e7;
              border-radius: 8px;
              width: 320px;
              height: 420px;
              display: flex;
              align-items: center;
              justify-content: center;
            `}
          >
            <ProgressiveImage
              placeholderSrc={getUrlWithToken(FacebookConversionAPI, token)}
              src={getUrlWithToken(FacebookConversionAPI, token)}
            />
          </div>
          <div
            css={css`
              flex: 1;
              padding: 12px 0 12px 20px;
              max-width: 350px;
            `}
          >
            <span
              css={css`
                border: 1px solid #e0e0e9;
                border-radius: 50px;
                padding: 2px 8px;
                display: inline-flex;
                align-items: center;
                font-size: 12px;
                margin-top: 0;
                font-weight: 500;
                color: #2d2d2d;
                background-color: #fff;
              `}
            >
              <ProgressiveImage
                placeholderSrc={getUrlWithToken(MetaConversionIcon, token)}
                src={getUrlWithToken(MetaConversionIcon, token)}
                imgProps={{
                  loading: 'lazy',
                  style: {
                    objectFit: 'cover',
                    display: 'block',
                    width: '44px',
                  },
                }}
              />
              <span
                css={css`
                  margin-left: 5px;
                `}
              >
                Conversion API integration
              </span>
            </span>
            <QSTypography
              css={css`
                font-weight: 600;
                font-size: 20px;
                overflow: hidden;
                line-height: 28px;
                margin: 16px 0;
              `}
            >
              🚀 Supercharge Your Ads ROI with Conversions API:{' '}
              <span
                css={css`
                  color: rgba(45, 45, 45, 0.7);
                `}
              >
                Businesses switching to CAPI have experienced
              </span>
            </QSTypography>

            <div
              css={css`
                flex: 1;
                display: flex;
                flex-direction: column;
                gap: 8px;
              `}
            >
              {FEATURES.map((feature, key) => (
                <div
                  key={key}
                  css={css`
                    display: inline-flex;
                    align-items: flex-start;
                    justify-content: flex-start;
                    font-weight: 500;
                    gap: 8px;
                  `}
                >
                  <div
                    style={{
                      width: '20px',
                      height: '20px',
                      display: 'block',
                    }}
                  >
                    <SingleTickIcon
                      color="#3F906D"
                      css={css`
                        width: 20px;
                        height: 20px;
                        vertical-align: top;
                      `}
                    />
                  </div>
                  <div
                    css={css`
                      font-family: Inter;
                      font-size: 12px;
                      font-weight: 500;
                      line-height: 16.94px;
                      text-align: left;
                      color: #2d2d2d;
                    `}
                  >
                    {feature?.featureLabel}
                  </div>
                </div>
              ))}
            </div>

            <div
              css={css`
                margin-top: 16px;
                display: flex;
                justify-content: space-between;
                align-items: center;
                gap: 8px;
              `}
            >
              <QSButton
                css={css`
                  height: 40px;
                  width: 100%;
                  white-space: nowrap;
                `}
                onClick={onClickContactUs}
              >
                {hideDTDetails ? (
                  'Contact Us'
                ) : (
                  <>
                    {purchaseLoading ? (
                      <Loader
                        size={16}
                        loaderProps={{
                          sx: {
                            color: 'white',
                          },
                        }}
                      />
                    ) : (
                      'Get Meta Conversion API Now'
                    )}
                  </>
                )}
              </QSButton>
            </div>
          </div>
        </div>
      </div>
    </QSModal>
  );
}
